import type { ShopLocale } from '../../../i18n'
import {
  CreditCardIcon,
  formatCount,
  MapPinIcon,
  PackageIcon,
  ShieldIcon,
  SupportIcon,
  TruckIcon,
} from './dashboardShared'
import type { KpiShortcutItem, QuickActionItem } from './dashboardTypes'

type BuildDashboardQuickActionCardsInput = {
  isItalian: boolean
  onNavigate: (targetPath: string) => void
  toLocalizedPath: (path: string) => string
}

export const buildDashboardQuickActionCards = ({
  isItalian,
  onNavigate,
  toLocalizedPath,
}: BuildDashboardQuickActionCardsInput): QuickActionItem[] => [
  {
    id: 'orders',
    icon: <PackageIcon className="h-4 w-4" />,
    title: isItalian ? 'I miei ordini' : 'My orders',
    description: isItalian
      ? 'Apri lo storico ordini e filtra rapidamente per stato.'
      : 'Open order history and quickly filter by status.',
    onClick: () => onNavigate(toLocalizedPath('/dashboard/orders')),
  },
  {
    id: 'addresses',
    icon: <MapPinIcon className="h-4 w-4" />,
    title: isItalian ? 'Indirizzi' : 'Addresses',
    description: isItalian
      ? 'Aggiorna indirizzi di fatturazione e spedizione usati al checkout.'
      : 'Update billing and shipping addresses used during checkout.',
    onClick: () => onNavigate(toLocalizedPath('/dashboard/addresses')),
  },
  {
    id: 'payments',
    icon: <CreditCardIcon className="h-4 w-4" />,
    title: isItalian ? 'Pagamenti e preferenze' : 'Payments and preferences',
    description: isItalian
      ? 'Gestisci impostazioni di pagamento, lingua e opzioni account.'
      : 'Manage payment settings, language, and account options.',
    onClick: () => onNavigate(toLocalizedPath('/dashboard/preferences')),
  },
  {
    id: 'profile-security',
    icon: <ShieldIcon className="h-4 w-4" />,
    title: isItalian ? 'Profilo, sicurezza e privacy' : 'Profile, security, and privacy',
    description: isItalian
      ? 'Controlla dati account, privacy e richieste assistenza.'
      : 'Review account details, privacy, and support requests.',
    onClick: () => onNavigate(toLocalizedPath('/dashboard/profile')),
  },
]

type BuildDashboardKpiShortcutsInput = {
  isItalian: boolean
  ordersLoading: boolean
  supportLoading: boolean
  activeOrdersCount: number
  inDeliveryCount: number
  openTicketsCount: number
  savedAddressesCount: number
  locale: ShopLocale
  onNavigate: (targetPath: string) => void
  toLocalizedPath: (path: string) => string
}

export const buildDashboardKpiShortcuts = ({
  isItalian,
  ordersLoading,
  supportLoading,
  activeOrdersCount,
  inDeliveryCount,
  openTicketsCount,
  savedAddressesCount,
  locale,
  onNavigate,
  toLocalizedPath,
}: BuildDashboardKpiShortcutsInput): KpiShortcutItem[] => [
  {
    id: 'active-orders',
    icon: <PackageIcon className="h-4 w-4" />,
    label: isItalian ? 'Ordini attivi' : 'Active orders',
    value: ordersLoading ? '...' : formatCount(activeOrdersCount, locale),
    hint: isItalian ? 'Apri ordini in lavorazione' : 'Open processing orders',
    onClick: () => onNavigate(toLocalizedPath('/dashboard/orders?status=created')),
  },
  {
    id: 'in-delivery',
    icon: <TruckIcon className="h-4 w-4" />,
    label: isItalian ? 'In consegna' : 'In delivery',
    value: ordersLoading ? '...' : formatCount(inDeliveryCount, locale),
    hint: isItalian ? 'Apri ordini spediti' : 'Open shipped orders',
    onClick: () => onNavigate(toLocalizedPath('/dashboard/orders?status=shipped')),
  },
  {
    id: 'open-tickets',
    icon: <SupportIcon className="h-4 w-4" />,
    label: isItalian ? 'Ticket aperti' : 'Open tickets',
    value: supportLoading ? '...' : formatCount(openTicketsCount, locale),
    hint: isItalian ? 'Vai in assistenza' : 'Open support inbox',
    onClick: () => onNavigate(toLocalizedPath('/dashboard/support')),
  },
  {
    id: 'saved-addresses',
    icon: <MapPinIcon className="h-4 w-4" />,
    label: isItalian ? 'Indirizzi salvati' : 'Saved addresses',
    value: formatCount(savedAddressesCount, locale),
    hint: isItalian ? 'Gestisci indirizzi' : 'Manage addresses',
    onClick: () => onNavigate(toLocalizedPath('/dashboard/addresses')),
  },
]
